import { useEffect, useState } from 'react'
import { Form, TimePicker, message, Input } from 'antd'
import { Select, Switch, Drawer } from '@/components/ui'
import { router } from '@inertiajs/react'
import axios from '@/lib/axios'
import dayjs from 'dayjs'
import '@/styles/compact-form.css'

interface Event {
  id: number
  slug: string
}

interface Track {
  id: number
  name: string
  color: string
}

interface AgendaDay {
  id: number
  scheduled_on: string
  tracks: Track[]
}

interface SessionSpeaker {
  id: number
  full_name: string
}

interface Session {
  id: number
  title: string
  description: string | null
  start_time: string | null
  end_time: string | null
  visible: boolean
  agenda_track_id: number
  agenda_venue_id: number | null
  speakers: SessionSpeaker[]
}

interface Speaker {
  id: number
  full_name: string
}

interface Venue {
  id: number
  title: string
}

interface SessionDrawerProps {
  event: Event
  selectedDay: AgendaDay
  preselectedTrackId: number | null
  session: Session | null
  speakers: Speaker[]
  venues: Venue[]
  open: boolean
  onClose: () => void
}

export default function SessionDrawer({
  event,
  selectedDay,
  preselectedTrackId,
  session,
  speakers,
  venues,
  open,
  onClose
}: SessionDrawerProps) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const trackId = session ? session.agenda_track_id : preselectedTrackId
  const selectedTrack = selectedDay.tracks.find(t => t.id === trackId)

  useEffect(() => {
    if (open) {
      if (session) {
        form.setFieldsValue({
          title: session.title,
          description: session.description,
          start_time: session.start_time ? dayjs(session.start_time, 'HH:mm') : null,
          end_time: session.end_time ? dayjs(session.end_time, 'HH:mm') : null,
          agenda_track_id: session.agenda_track_id,
          agenda_venue_id: session.agenda_venue_id,
          speaker_ids: session.speakers.map(s => s.id),
          visible: session.visible
        })
      } else {
        form.resetFields()
        form.setFieldsValue({
          agenda_track_id: preselectedTrackId,
          visible: true
        })
      }
    }
  }, [open, session, preselectedTrackId, form])

  const handleSubmit = async (values: any) => {
    setLoading(true)

    try {
      const payload = {
        session: {
          title: values.title,
          description: values.description || null,
          start_time: values.start_time ? values.start_time.format('HH:mm') : null,
          end_time: values.end_time ? values.end_time.format('HH:mm') : null,
          agenda_track_id: values.agenda_track_id,
          agenda_venue_id: values.agenda_venue_id || null,
          speaker_ids: values.speaker_ids || [],
          visible: values.visible
        }
      }

      let response
      if (session) {
        response = await axios.patch(
          `/events/${event.slug}/agenda/sessions/${session.id}`,
          payload
        )
      } else {
        response = await axios.post(
          `/events/${event.slug}/agenda/sessions`,
          payload
        )
      }

      if (response.data.success) {
        message.success(response.data.message)
        router.reload()
        onClose()
      }
    } catch (error: any) {
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to save session')
    } finally {
      setLoading(false)
    }
  }

  const trackOptions = selectedDay.tracks.map(track => ({
    label: track.name,
    value: track.id
  }))

  const speakerOptions = speakers.map(speaker => ({
    label: speaker.full_name,
    value: speaker.id
  }))

  const venueOptions = venues.map(venue => ({
    label: venue.title,
    value: venue.id
  }))

  return (
    <Drawer
      title={session ? 'Edit Session' : 'Add Session'}
      open={open}
      onClose={onClose}
      onSubmit={() => form.submit()}
      submitText={session ? 'Update' : 'Add'}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ visible: true }}
        className="compact-form"
      >
        <div className="mb-3 p-2 bg-gray-50 rounded border border-gray-200 text-sm flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Track:</span>
            <span className="font-medium" style={{ color: selectedTrack?.color }}>
              {selectedTrack?.name}
            </span>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <span className="text-gray-600">Date:</span>
            <span className="font-medium">
              {dayjs(selectedDay.scheduled_on).format('D MMMM YYYY')}
            </span>
          </div>
        </div>

        <Form.Item name="agenda_track_id" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          label="Session Title"
          name="title"
          rules={[{ required: true, message: 'Please enter session title' }]}
        >
          <Input placeholder="Enter session title" size="small" />
        </Form.Item>

        <Form.Item label="Session Description" name="description">
          <Input.TextArea
            placeholder="Enter description"
            rows={4}
            maxLength={400}
            showCount
            size="small"
          />
        </Form.Item>

        <div className="grid grid-cols-2 gap-3">
          <Form.Item label="Start time" name="start_time">
            <TimePicker
              format="HH:mm"
              placeholder="Start time"
              className="w-full"
              showNow={false}
              size="small"
            />
          </Form.Item>

          <Form.Item label="End time" name="end_time">
            <TimePicker
              format="HH:mm"
              placeholder="End time"
              className="w-full"
              showNow={false}
              size="small"
            />
          </Form.Item>
        </div>

        <Form.Item label="Venue" name="agenda_venue_id">
          <Select
            placeholder="Select"
            allowClear
            options={venueOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            size="small"
          />
        </Form.Item>

        <Form.Item label="Speaker" name="speaker_ids">
          <Select
            mode="multiple"
            placeholder="Select"
            options={speakerOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            size="small"
          />
        </Form.Item>

        <Form.Item
          label="Visibility"
          name="visible"
          valuePropName="checked"
          help="Enable public view for this session?"
          style={{ marginBottom: 0 }}
        >
          <Switch  />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
